import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import { useFormSetValue, useFormError } from '@/Hooks';
import { ClassNameProps, DataProps, DisabledProps, InputRefProps } from '@/Types';
import { ErrorTooltip, errorTooltipClass } from '@/Components/Controls/ErrorTooltip/ErrorTooltip';
import styles from './Input.scss';
import { v4 as uuidV4 } from 'uuid';
import { Icons } from '@/Static/IconsRes';

export type Props<TFormData extends object> = {
  type?: 'text' | 'password' | 'number';
  placeholder?: string;
  autocomplete?: 'off' | 'new-password';
  hidden?: boolean;
  step?: number;
  min?: number;
  max?: number;
  inputClassName?: string;
  onChange?: (val: string) => void;
  dataAttributes?: DataProps;
  onFocus?: () => void;
  autoFocus?: boolean;
} & ClassNameProps &
  DisabledProps &
  InputRefProps<HTMLInputElement, TFormData, string | number>;

export function FormInput<TFormData extends object>({
  name,
  register,
  rules,
  setValue,
  valueForSet = '',
  placeholder,
  error,
  readOnly = false,
  disabled = false,
  type = 'text',
  autocomplete = 'off',
  hidden = false,
  className,
  inputClassName,
  onBlur,
  step = 1,
  min,
  max,
  onChange,
  onFocus,
  autoFocus = false,
  dataAttributes,
}: Props<TFormData>) {
  useFormSetValue(name, valueForSet, setValue);
  const errorMessage = useFormError(name, error);

  const inputId = useMemo(() => uuidV4(), []);
  const inputRef = register?.(name as any, rules);
  const [inputType, setInputType] = useState(type);

  const input = (
    <input
      {...inputRef}
      id={inputId}
      name={name as string}
      defaultValue={valueForSet}
      placeholder={placeholder}
      autoComplete={autocomplete}
      onBlur={onBlur}
      onFocus={onFocus}
      readOnly={disabled || readOnly}
      disabled={disabled}
      hidden={hidden}
      type={inputType}
      step={step}
      autoFocus={autoFocus}
      onChange={(e) => {
        inputRef?.onChange(e);

        if (type == 'number' && min && Number(e.target.value) < min) {
          e.target.value = min.toString();
        }

        if (type == 'number' && max && Number(e.target.value) > max) {
          e.target.value = max.toString();
        }

        onChange?.(e.target.value);
      }}
      onKeyDown={(e) => {
        if (type == 'number' && ['e', 'E'].includes(e.key)) {
          e.preventDefault();
        }
      }}
      min={min}
      max={max}
      className={cn(styles.input, inputClassName, errorMessage ? errorTooltipClass : null)}
      {...dataAttributes}
    />
  );

  const onUp = () => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    input.stepUp(step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const onDown = () => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    input.stepDown(step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  return hidden ? (
    input
  ) : (
    <ErrorTooltip error={errorMessage} className={className}>
      <span className={styles.container}>
        {input}
        {type == 'number' && !disabled && (
          <>
            <button type={'button'} className={styles.increase} onClick={onUp} />
            <button type={'button'} className={styles.decrease} onClick={onDown} />
          </>
        )}

        {type == 'password' && (
          <label
            className={styles.showPassword}
            onClick={() => setInputType(inputType == 'password' ? 'text' : 'password')}
          >
            {inputType == 'text' ? <img src={Icons.Show} alt={'show'} /> : <img src={Icons.Hide} alt={'hide'} />}
          </label>
        )}
      </span>
    </ErrorTooltip>
  );
}
